import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Briefcase, Palette, Globe2, Send, FileText, Settings2, LogOut, Users, Coffee
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/standup", label: "Studio Standup", icon: Coffee, testId: "nav-standup" },
  { to: "/agenda", label: "Daily Agenda", icon: CalendarDays, testId: "nav-agenda" },
  { to: "/jobs", label: "Jobs & Orders", icon: Briefcase, testId: "nav-jobs" },
  { to: "/artwork", label: "Artwork Tracker", icon: Palette, testId: "nav-artwork" },
  { to: "/website", label: "Website Updates", icon: Globe2, testId: "nav-website" },
  { to: "/sendoffs", label: "Customer Send-Off", icon: Send, testId: "nav-sendoffs" },
  { to: "/quotes", label: "Quotes", icon: FileText, testId: "nav-quotes" },
  { to: "/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/setup", label: "Setup Lists", icon: Settings2, testId: "nav-setup" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background paper-grain flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border/70 bg-card/50 flex flex-col" data-testid="sidebar">
        <div className="px-6 py-7 border-b border-border/60">
          <div className="label-caps">Lowcountry Studio</div>
          <div className="font-heading text-2xl mt-2 leading-tight tracking-tight">
            Workflow <span className="font-script text-espresso">Ledger</span>
          </div>
          <div className="mt-4 flex items-center gap-3 opacity-80">
            <img
              src="https://customer-assets.emergentagent.com/job_task-command-center-25/artifacts/cpload9r_JTD.png"
              alt="JTD"
              className="h-8 w-auto object-contain"
            />
            <span className="h-6 w-px bg-border/70" />
            <img
              src="https://customer-assets.emergentagent.com/job_task-command-center-25/artifacts/0sk7wcfv_Sweet%20CHS%20LOGO%20.png"
              alt="Sweet CHS"
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {links.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-secondary text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-border/60">
          <div className="mb-3">
            <div className="label-caps">Signed in as</div>
            <div className="font-heading text-base mt-0.5" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={doLogout}
            className="w-full"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
